import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { WeeklySchedule } from 'src/screens/OnboardingTrainer/screens/Schedule/utils/timezoneUtils';
import { UnavailableDateEntry, ConnectedCalendarInfo } from 'src/api/queries/useGetTrainerSchedule';
import { ScheduleConflict } from './useCheckScheduleConflicts';

export interface UpdateSchedulePayload {
  uid: string;
  timezone?: string;
  weekly_schedule?: WeeklySchedule;
  days?: number;
  force?: boolean;
  ai_responses_enabled?: boolean;
  ai_responses_only?: boolean;
  unavailable_dates?: UnavailableDateEntry[];
  unavailable_dates_only?: boolean;
  connected_calendar?: ConnectedCalendarInfo;
  calendar_connection_only?: boolean;
}

export interface UpdateScheduleResponse {
  status: string;
  hasConflicts?: boolean;
  conflicts?: ScheduleConflict[];
  message?: string;
}

export function useUpdateTrainerSchedule() {
  const queryClient = useQueryClient();

  return useMutation<UpdateScheduleResponse, Error, UpdateSchedulePayload>({
    mutationFn: async (payload: UpdateSchedulePayload) => {
      const {
        uid,
        timezone,
        weekly_schedule,
        days = 60,
        force = false,
        ai_responses_enabled,
        ai_responses_only,
        unavailable_dates,
        unavailable_dates_only,
        connected_calendar,
        calendar_connection_only,
      } = payload;

      if (!uid) {
        throw new Error('User UID is required to update schedule');
      }

      const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_UPDATE_SCHEDULE}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid,
          timezone,
          weekly_schedule,
          days,
          force,
          ai_responses_enabled,
          ai_responses_only,
          unavailable_dates,
          unavailable_dates_only,
          connected_calendar,
          calendar_connection_only,
        }),
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to update schedule');
      }

      return response.json();
    },
    onSuccess: (res, variables) => {
      if (res?.hasConflicts) {
        return; // Don't hydrate cache if there were unresolved conflicts
      }

      const scheduleQueryKey = ['trainer-schedule', variables.uid];
      const onboardingQueryKey = ['onboarding-state', variables.uid];

      // Update schedule query cache immediately
      queryClient.setQueryData(scheduleQueryKey, (oldData: any) => {
        const prevData = oldData?.data || {};
        return {
          status: 'ok',
          data: {
            timezone: variables.timezone ?? prevData.timezone ?? '',
            weekly_schedule: variables.weekly_schedule ?? prevData.weekly_schedule ?? {},
            ai_responses_enabled:
              variables.ai_responses_enabled !== undefined
                ? variables.ai_responses_enabled
                : (prevData.ai_responses_enabled ?? true),
            unavailable_dates:
              variables.unavailable_dates !== undefined
                ? variables.unavailable_dates
                : (prevData.unavailable_dates ?? []),
            connected_calendar:
              variables.connected_calendar !== undefined
                ? variables.connected_calendar
                : (prevData.connected_calendar ?? null),
          },
        };
      });

      // Update onboarding query cache
      queryClient.setQueryData(onboardingQueryKey, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          onboarding: {
            ...oldData.onboarding,
            step4_schedule: {
              timezone: variables.timezone ?? oldData.onboarding?.step4_schedule?.timezone,
              weekly_schedule:
                variables.weekly_schedule ?? oldData.onboarding?.step4_schedule?.weekly_schedule,
            },
          },
          profile: {
            ...oldData.profile,
            timezone: variables.timezone ?? oldData.profile?.timezone,
            weekly_schedule: variables.weekly_schedule ?? oldData.profile?.weekly_schedule,
          },
        };
      });

      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
      queryClient.invalidateQueries({ queryKey: onboardingQueryKey });
    },
  });
}
